import { get, writable } from "svelte/store";
import { Authenticator } from "./authentication/Authenticator";
import { Api } from "./api/Api";
import { UserDataPlanTools } from "./authentication/plans/UserDataPlanTools";
import { BusyWorkManager } from "./shared/busy-work/BusyWorkManager";
import { WebStorageStateStore } from "oidc-client-ts";
import { RoutingFacade } from "./lib/routing/RoutingFacade";
export class AppManager {
    constructor() {
        this.currentUser = writable(undefined);
        this.currentUserIsAdmin = writable(false);
        this.lastError = writable(undefined);
        this.canOpenProject = writable(true);
        this.isDesktop = writable(true);
        this.busyManager = new BusyWorkManager();
        this.profiles = [];
        this.ModalHooks = {};
    }
    /**
     * Initializes the app with the given settings.
     *
     * @param settings The settings.
     */
    initialize(settings) {
        this.apiKeys = settings.apiKeys;
        this.profiles = settings.profiles;
        let publicUrl = settings.publicUrl.href;
        let rootUrl = settings.publicUrl.origin;
        if (publicUrl.endsWith("/"))
            publicUrl = publicUrl.substring(0, publicUrl.length - 1);
        if (rootUrl.endsWith("/"))
            rootUrl = rootUrl.substring(0, rootUrl.length - 1);
        // initialize the authenticator.
        this.authenticator = new Authenticator({
            authority: "process.env.AUTHORITY",
            client_id: "website-webapp",
            redirect_uri: `${publicUrl}/callback`,
            post_logout_redirect_uri: `${publicUrl}`,
            silent_redirect_uri: `${publicUrl}/silent`,
            response_type: "code",
            scope: "openid profile impact roles identity shortcut",
            automaticSilentRenew: true,
            monitorSession: false,
            userStore: new WebStorageStateStore({
                prefix: "anyways",
                store: window.localStorage,
            }),
        });
        // make sure we sync states.
        this.isDesktop.subscribe(async () => {
            await this.updateCanOpenProject();
        });
        this.currentUser.subscribe(async () => {
            await this.updateCanOpenProject();
        });
        this.currentUser.subscribe(result => {
            if (typeof result === "undefined") {
                this.currentUserIsAdmin.set(false);
                return;
            }
            this.currentUserIsAdmin.set(result.isAdmin);
        });
        // set convenience url properties.
        this.RootUrl = settings.publicUrl.origin + "/";
        this.PublicUrl = settings.publicUrl.href + "/";
        // setup apis.
        this.api = new Api(this.authenticator, settings.apiSettings);
        this.routingFacade = new RoutingFacade();
    }
    /**
     * A boolean indicating if the app is currently on desktop or not.
     */
    getIsDesktop() {
        return this.isDesktop;
    }
    /**
     * Gets the busy manager.
     */
    getBusyWorkManager() {
        return this.busyManager;
    }
    /**
     * Gets a store with the can open projects permissions.
     */
    getCanOpenProjects() {
        return this.canOpenProject;
    }
    async updateCanOpenProject() {
        const isDesktop = get(this.isDesktop);
        const hasActivePlan = await this.hasActivePlan();
        this.canOpenProject.set(isDesktop && hasActivePlan);
    }
    /**
     * Gets the current user, if any, in the form of a readable store.
     */
    async getCurrentUserStore() {
        if (typeof get(this.currentUser) === "undefined") {
            if (!(await this.authenticator.isSignedIn())) {
                return this.currentUser;
            }
            const user = await this.authenticator.getUserDetails();
            this.currentUser.set(user);
        }
        if (!(await this.authenticator.isSignedIn())) {
            this.currentUser.set(undefined);
            return this.currentUser;
        }
        return this.currentUser;
    }
    /**
     * Gets a store reflecting wether the current store is an admin or not.
     */
    getCurrentUserAdminStore() {
        return this.currentUserIsAdmin;
    }
    /**
     * Gets the current plan.
     * @returns The current plan, if any.
     */
    async getPlanState() {
        const user = get(await this.getCurrentUserStore());
        if (typeof user === "undefined")
            return undefined;
        return UserDataPlanTools.getPlanState(user);
    }
    /**
     * Gets the active plan state.
     */
    async hasActivePlan() {
        const planState = await this.getPlanState();
        if (typeof planState === "undefined")
            return false;
        return planState.hasActivePlan();
    }
    /**
     * Checks if the user is signed in.
     * @returns True if a user is signed in, false otherwise.
     */
    async isSignedIn() {
        return this.authenticator.isSignedIn();
    }
    /**
     * Start sign in.
     * @param url The url to redirect to once signed in.
     */
    async startSignin(url) {
        await this.authenticator.startSignin(url);
    }
    /**
     * Logs the current user out.
     */
    async logout() {
        await this.authenticator.logout();
    }
    /**
     * Completes a signin using a redirect callback.
     * @returns the url to redirect to
     */
    async completeSignin() {
        return await this.authenticator.completeSignin();
    }
    /**
     * Completes a signin using a silent callback.
     */
    async completeSigninSilent() {
        return await this.authenticator.signinSilentCallback();
    }
    /**
     * An error has occured that needs reporting to the user.
     */
    setError(error) {
        this.lastError.set(error);
    }
}
export const appManager = new AppManager();
